import styles from '@/components/static/StudyMaterialsGuidePage/StudyMaterialsGuidePage.module.scss'
import React, { ReactElement } from 'react'
import { GuideComponent } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/GuideComponent.types'
import { reusableCssClass } from '@/utils/reusableCssClasses'
import Tooltip from 'antd/lib/tooltip'
import { UiLayoutConstant } from '@/globals/uiLayoutConstant'
import { BaseGuideComponent } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/BaseGuideComponent/BaseGuideComponent'
import { Screenshot } from '@/components/static/images/Screenshot/Screenshot'
import { StudyMaterialsGuideExternalLink } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/StudyMaterialsGuideExternalLink/StudyMaterialsGuideExternalLink'
import { LinkIfLoggedInOtherwiseText } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/LinkIfLoggedInOtherwiseText/LinkIfLoggedInOtherwiseText'
import { BackToTop } from '@/components/static/BackToTop/BackToTop'
import { ClickableArrow } from '@/components/ClickableArrow/ClickableArrow'
import { OwlPageHeader } from '@/components/OwlPageHeader/OwlPageHeader'
import { QuizOwl } from '@/components/static/images/owlBranding/quizOwl'
import { getAbbreviationName } from '@/components/static/StudyMaterialsGuidePage/utils'

export const PracticeProblemsGuidePage: React.FC<GuideComponent> = (
    props
): ReactElement => {
    return (
        <div className={styles.studyMaterialsGuidePageBackground}>
            <BackToTop isLoggedIn={props.isLoggedIn} />

            <div className={styles.studyMaterialsGuidePage}>
                <div className={styles.headerContainer}>
                    <div className={reusableCssClass.centerChildrenVertically}>
                        <Tooltip
                            title={'Return to Overview'}
                            placement={'right'}
                        >
                            <ClickableArrow
                                orientation={'left'}
                                linkDestination={
                                    UiLayoutConstant.STUDY_MATERIALS_GUIDE_PAGE
                                }
                            />
                        </Tooltip>
                    </div>
                    <div className={styles.owlHeader}>
                        <OwlPageHeader
                            text={'Practice Questions'}
                            owlComponent={<QuizOwl />}
                        />
                    </div>
                </div>

                <BaseGuideComponent
                    bulletSide={'left'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>Summary</div>
                            <p>
                                Math questions have historically comprised ~40%
                                of the points on each exam. While math questions
                                are more time-consuming than notecard questions
                                per point, they comprise far too many overall
                                points to not spend a significant amount of time
                                preparing.
                            </p>
                            <p>
                                Similar to the lower level exams, at a basic
                                level there are a finite number of computations
                                that are testable from the syllabus.&nbsp;
                                {getAbbreviationName(props.courseConfigType)}
                                &apos;s content database covers all of these
                                several times over, providing you with practice
                                applying the same math concepts in different
                                scenarios, much like a real exam.
                            </p>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/problemsDashboardWithTimerScreenshot.webp'
                                }
                                alt={'screenshot of questions dashboard'}
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'right'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>How to Use</div>
                            <ol>
                                <li>
                                    Focus your time here 1-2 months prior to the
                                    exam sitting date or when you need a break
                                    from notecards.
                                </li>
                                <li>
                                    Practice questions include both mathematical
                                    and conceptual questions. The custom quiz
                                    build tool is the best way to focus on one
                                    versus the other.
                                </li>
                                <li>
                                    Complete both full-length timed exams and
                                    shorter, customized quizzes. We recommend
                                    doing shorter quizzes to learn most of the
                                    questions types, and doing a few full-length
                                    timed exams closer to the exam to build up
                                    your mental stamina.
                                </li>
                                <li>
                                    Use the provided Excel templates to practice
                                    doing questions in an environment as close
                                    to an actual exam as possible and upload
                                    your completed work to review or access
                                    later.
                                </li>
                                <li>
                                    Hit ✨ Autograde on any question — or on a
                                    whole quiz at once — for point-by-point
                                    feedback on how your answer compares to the
                                    official solution. Upload your Excel
                                    workbook and you get it back marked up, with
                                    notes on the cells that went wrong.
                                </li>
                                <li>
                                    Use the questions dashboard analytics along
                                    with the custom quiz build tool to spend
                                    more time on questions you haven&apos;t yet
                                    mastered.
                                </li>
                            </ol>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/practiceProblemSolutionScreenshot.webp'
                                }
                                alt={
                                    'screenshot of a practice question solution'
                                }
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'left'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>
                                Questions Breakdown
                            </div>
                            <p>
                                To aid with your studying, practice questions
                                are broken down by the following dimensions:
                            </p>
                            <ol>
                                <li>
                                    <b>Topic</b>&nbsp;- Location within the
                                    syllabus. There are three tiers total within
                                    the{' '}
                                    <StudyMaterialsGuideExternalLink
                                        href={
                                            'https://www.soa.org/4ab6a3/globalassets/assets/files/edu/2023/spring/syllabi/ghdp.pdf'
                                        }
                                        text={'syllabus tree'}
                                    />
                                    : topic, source, and chapter.
                                </li>
                                <li>
                                    <b>User History</b>&nbsp;- Whether the
                                    question has been done by the user before,
                                    as well as whether the previous attempt was
                                    correct or incorrect.
                                </li>
                                <li>
                                    <b>Question Type</b>&nbsp;- You can filter
                                    exams that came from a previous exam by the
                                    specific exam sitting it came from, as well
                                    as whether the question has been marked by
                                    our course instructors as a &quot;Gold
                                    Standard&quot; question (concise, effective
                                    demonstration of the math concept being
                                    tested). Not mutually exclusive.
                                </li>
                                <li>
                                    <b>Math vs. Conceptual</b>&nbsp;- Whether
                                    the question is mathematical or conceptual
                                    in nature. Not mutually exclusive within
                                    multi-part questions.
                                </li>
                                <li>
                                    <b>Previous Exam</b>&nbsp;- Whether the
                                    question comes from a previous exam or not.
                                </li>
                                <li>
                                    <b>Gold Standard</b>&nbsp;- Gold standard
                                    questions are generally simpler questions
                                    that focus solely on a specific testable
                                    math concept. This isolates the complexity
                                    of the question into something that can be
                                    more easily studied and mastered.
                                </li>
                                <li>
                                    <b>Bookmarked vs. Starred</b>&nbsp;- You can
                                    both bookmark and star individual questions
                                    so that you can more easily come back to
                                    them later. What is the difference between
                                    the two? Bookmarking allows you to come back
                                    to the question DURING the same quiz / exam,
                                    whereas starring allows you to come back
                                    AFTER the quiz / exam.
                                </li>
                            </ol>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/problemTopicMasteryScreenshot.webp'
                                }
                                alt={'screenshot of question topic mastery'}
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'right'}
                    bullets={
                        <>
                            <div className={styles.title}>
                                General Guidelines
                            </div>
                            When working through a question set:
                            <ul>
                                <li>
                                    Do not look at the answer until you have
                                    fully attempted to answer the question.
                                </li>
                                <li>
                                    Use the answer box provided, or pen and
                                    paper, to answer the question.
                                </li>
                                <li>
                                    <span
                                        className={
                                            styles.studyMaterialsGuideEmphasis
                                        }
                                    >
                                        Partial credit adds up
                                    </span>{' '}
                                    - in order to receive as much partial credit
                                    as possible, practice showing as much work
                                    as possible. A good trick is to revisit old
                                    question sets at least a week after working
                                    on them to see if you can follow your own
                                    work. If you can&apos;t, there is little to
                                    no chance a grader will be able to.
                                </li>
                                <li>
                                    Whenever a question provides an Excel
                                    template, use the template to practice doing
                                    Excel questions in Excel. Just like in a
                                    real exam, you can upload your Excel work
                                    back to the question to use when grading.
                                </li>
                            </ul>
                        </>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/quizOverviewScreenshot.webp'
                                }
                                alt={'screenshot of quiz overview'}
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'left'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>Extra Goodies</div>
                            <ul>
                                <li>
                                    Use the{' '}
                                    <LinkIfLoggedInOtherwiseText
                                        text={'practice questions dashboard'}
                                        relativePath={
                                            UiLayoutConstant.PRACTICE_PROBLEMS_DASHBOARD
                                        }
                                        isLoggedIn={props.isLoggedIn}
                                    />{' '}
                                    to view your progress and determine which
                                    questions to focus on next. The &quot;Topic
                                    Mastery By Topic&quot; section is a great
                                    place to determine which sections you
                                    haven&apos;t yet mastered.
                                </li>
                                <li>
                                    If you are just starting out, click the
                                    &quot;Build Custom Quiz&quot; button and
                                    generate a few randomized practice quizzes
                                    of math questions only.
                                </li>
                                <li>
                                    Want to skip over a question and come back
                                    to it later? You can use the &quot;Overview
                                    Page&quot; to quickly travel to different
                                    questions, automatically marked as complete
                                    or incomplete to aid navigation.
                                </li>
                                <li>
                                    As you complete more quizzes, use the fine
                                    level of detail available in the quiz
                                    builder tool to focus your efforts on areas
                                    where you have not yet performed well.
                                </li>
                                <li>
                                    If you want to add a specific question to a
                                    quiz, click the &quot;Search & Add&quot;
                                    button at the bottom. You can use free-text
                                    search to find the question based on what
                                    you remember of it.
                                </li>
                                <li>
                                    Use the history tool at the bottom of the
                                    dashboard to revisit old question sets,
                                    regardless of whether they are still in
                                    progress or previously completed. Our system
                                    tags question sets to help you tell them
                                    apart.
                                </li>
                                <li>
                                    Having trouble keeping the various formulas
                                    straight? Download our formula sheet to
                                    specifically target memorizing and
                                    understanding formulas, and use at your
                                    convenience.
                                </li>
                                <li>
                                    Have a question about a specific problem?
                                    Submit a comment to a problem&apos;s
                                    dedicated comment thread and get a response
                                    from your instructor in less than 24 hours.
                                </li>
                            </ul>
                        </div>
                    }
                    nonBullet={
                        <div className={styles.nonBulletSection}>
                            <Screenshot
                                src={
                                    '/studyMaterialsGuide/mathProblemScreenshot.webp'
                                }
                                alt={'screenshot of math question'}
                            />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
            </div>
        </div>
    )
}
